/**
 * Fetch a list once per brokerage the caller belongs to, and merge the results.
 *
 * A dual-licensed agent is one human with one login and two `users` rows, one
 * per brokerage. Every list endpoint is tenant-scoped, so "show me my clients"
 * has always meant "my clients in whichever brokerage I last toggled to" —
 * making them switch back and forth to answer a question about their own book.
 *
 * This runs the same query once per membership, concurrently, each through an
 * ApiClient bound to a different tenant (`withActingTenant`), and tags each row
 * with the membership it came from so the UI can badge it. The fetcher receives
 * a client, so a merged call site reads exactly like the single-tenant one.
 *
 * Two things that must not be got wrong:
 *
 * 1. **The query key includes the tenant.** Without it React Query treats the
 *    per-tenant fetches as ONE cache entry, and the second brokerage's rows
 *    silently overwrite the first's — a wrong-data bug that looks like a
 *    flicker.
 * 2. **Single-membership staff take the plain path.** No header, no fan-out;
 *    the overwhelmingly common case must not pay for the rare one, and it keeps
 *    the request identical to what it was before this existed.
 */

import { useQueries } from "@tanstack/react-query";
import type { ApiClient, TenantMembership } from "@re-platform/api-client";

import { api } from "./api";

/** A row plus the brokerage it was fetched from. */
export interface Scoped<T> {
  row: T;
  tenant: TenantMembership;
}

export interface MergedResult<T> {
  rows: Scoped<T>[];
  isPending: boolean;
  isError: boolean;
  error: Error | null;
  /** True when the caller belongs to more than one brokerage — badge the rows. */
  isDual: boolean;
}

/**
 * @param memberships the caller's active brokerages (see `useMemberships`)
 * @param key         base query key; the tenant id is appended per fetch
 * @param fetcher     receives an ApiClient already pinned to that brokerage
 */
export function useMergedQuery<T>(
  memberships: TenantMembership[],
  key: readonly unknown[],
  fetcher: (client: ApiClient) => Promise<T[]>,
  options?: { enabled?: boolean; actingTenant?: boolean },
): MergedResult<T> {
  const enabled = options?.enabled ?? true;
  // X-Acting-Tenant-Id fails CLOSED — it demands an active membership in the
  // target tenant (PR #94). A super_admin has no memberships at all, so sending
  // it 403s them out of their own console. They scope by ?tenant_id= instead,
  // which admin-svc resolves for a super_admin, so pass `actingTenant: false`.
  const actingTenant = options?.actingTenant ?? true;

  const results = useQueries({
    queries: memberships.map((m) => ({
      // Tenant in the key — see note 1 above.
      queryKey: [...key, m.tenant_id],
      queryFn: () =>
        fetcher(actingTenant ? api.withActingTenant(m.tenant_id) : api),
      enabled,
      retry: false,
    })),
  });

  const rows: Scoped<T>[] = [];
  results.forEach((result, i) => {
    const membership = memberships[i];
    if (!membership || !result.data) return;
    for (const row of result.data) rows.push({ row, tenant: membership });
  });

  // One brokerage failing must not blank the other's rows: report the error so
  // the screen can say something, but still render what did load. A dual agent
  // whose mortgage side is briefly 500ing should still see their real-estate
  // clients.
  const failed = results.find((r) => r.isError);

  return {
    rows,
    isPending: results.some((r) => r.isPending) && enabled,
    isError: failed !== undefined,
    error: (failed?.error as Error | undefined) ?? null,
    isDual: memberships.length > 1,
  };
}

/** Short brokerage label for a badge — "Mortgage" / "Real estate". */
export function brokerageLabel(m: TenantMembership): string {
  return m.brokerage_type === "MORTGAGE" ? "Mortgage" : "Real estate";
}

/**
 * The client an ACTION on a merged row must go through.
 *
 * The read fanned out across brokerages, so a row on screen may belong to
 * either one — and a mutation sent as the wrong tenant either 404s or, worse,
 * lands on the wrong brokerage's record. Every action on a merged list must be
 * addressed to the tenant its row came from, not to whatever the sidebar
 * happens to be showing.
 *
 * Single-membership staff deliberately keep the plain client: they have exactly
 * one tenant, the header would be redundant, and not sending it keeps their
 * requests byte-identical to what they were before merging existed.
 */
export function actingClientFor(
  tenantId: string | undefined,
  isDual: boolean,
): ApiClient {
  return isDual && tenantId ? api.withActingTenant(tenantId) : api;
}
